"use client";
import React, { useState, useEffect } from "react";

const CandidateCard = ({ candidate, onReject }) => {
  const {
    full_name,
    name,
    email,
    experience,
    job_title,
    Resume_Score,
    screening_overall_score,
    coding_overall_score,
    personality_overall_score,
    behavioral_overall_score,
    job_id,
    isShortlisted,
    isRejected
  } = candidate;

  const [shortlisted, setShortlisted] = useState(isShortlisted || false);

  const handleToggleShortlist = async (candidateId, currentStatus, job_id) => {
    const newStatus = !currentStatus;
    try {
      const response = await fetch(`/api/update-shortlist?jobId=${job_id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ candidateId, isShortlisted: newStatus }),
      });
      
      if (response.ok) {
        setShortlisted(newStatus);
      }
    } catch (error) {
      console.error("Error updating shortlist status:", error);
    }
  };

  const handleToggleReject = async (candidateId, job_id) => {
    try {
      const response = await fetch(`/api/update-reject?jobId=${job_id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ candidateId }),
      });

      if (response.ok) {
        onReject(candidateId);
      }
    } catch (error) {
      console.error("Error updating reject status:", error);
    }
  };

  return (
    <div className="candidate-card-cf">
      <img
        loading="lazy"
        src={"user.jpg"}
        alt="Avatar"
        className="candidate-image-cf"
      />
      <div className="candidate-info-cf">
        <div className="info-row-cf">
          <span className="info-label-cf">Name:</span>
          <span className="info-value-cf">{full_name || name || "N/A"}</span>
        </div>
        <div className="info-row-cf">
          <span className="info-label-cf">Email:</span>
          <span className="info-value-cf">{email}</span>
        </div>
        {experience && (
          <div className="info-row-cf">
            <span className="info-label-cf">Experience:</span>
            <span className="info-value-cf">{experience} years</span>
          </div>
        )}
        {job_title && (
          <div className="info-row-cf">
            <span className="info-label-cf">Applied To:</span>
            <span className="info-value-cf">{job_title}</span>
          </div>
        )}
        {Resume_Score && (
          <div className="info-row-cf">
            <span className="info-label-cf">Resume Score:</span>
            <span className="info-value-cf">{Resume_Score}%</span>
          </div>
        )}
        {screening_overall_score && (
          <div className="info-row-cf">
            <span className="info-label-cf">Screening Score:</span>
            <span className="info-value-cf">{screening_overall_score}%</span>
          </div>
        )}
        {personality_overall_score && (
          <div className="info-row-cf">
            <span className="info-label-cf">Personality Score:</span>
            <span className="info-value-cf">{personality_overall_score}%</span>
          </div>
        )}
        {behavioral_overall_score && (
          <div className="info-row-cf">
            <span className="info-label-cf">Behavioral Score:</span>
            <span className="info-value-cf">{behavioral_overall_score}%</span>
          </div>
        )}
        {coding_overall_score && (
          <div className="info-row-cf">
            <span className="info-label-cf">Coding Score:</span>
            <span className="info-value-cf">{coding_overall_score}%</span>
          </div>
        )}
      </div>
      <div className="action-buttons-cf">
        <button
          className={`btn-cf primary-cf ${shortlisted ? "shortlisted-cf" : ""}`}
          onClick={() => handleToggleShortlist(email, shortlisted, job_id)}
        >
          {shortlisted ? "Shortlisted" : "Shortlist"}
        </button>
        <button
          className={`btn-cf destructive-cf ${isRejected ? "delete-cf" : ""}`}
          onClick={() => handleToggleReject(email, job_id)}
        >
          {isRejected ? "Delete" : "Reject"}
        </button>
      </div>
    </div>
  );
};

const CandidateFollowup = ({ maxHeight }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    job_title: 'All Jobs',
    page: 1,
    per_page: 10
  });
  const [totalCount, setTotalCount] = useState(0);
  const [availableJobs, setAvailableJobs] = useState([]);

  const handleReject = (candidateId) => {
    setCandidates(prevCandidates =>
      prevCandidates.filter(candidate => candidate.email !== candidateId)
    );
  };

  const fetchAvailableJobs = async () => {
    try {
      const response = await fetch('/api/employer-jobs');
      if (response.ok) {
        const jobs = await response.json();
        setAvailableJobs(jobs);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  };

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.job_title && filters.job_title !== 'All Jobs') {
        params.append('job_title', filters.job_title);
      }
      params.append('page', filters.page);
      params.append('per_page', filters.per_page);
      
      const response = await fetch(`/api/candidatelist_without_jobID?${params}`);
      
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      
      const data = await response.json();
      setCandidates(data.candidates || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailableJobs();
  }, []);

  useEffect(() => {
    fetchCandidates();
  }, [filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  if (loading) {
    return <div className="loading">Loading candidates...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="comp-recent-jobs-container">
      <div className="header-section-cf">
        <h2 className="header-title-cf">Candidate Details</h2>
        <div className="header-divider-cf"></div>
        
        {/* Filters */}
        <div className="filters-container">
          <div className="filter-group">
            <label htmlFor="job-filter">Filter by Job:</label>
            <select 
              id="job-filter"
              value={filters.job_title}
              onChange={(e) => handleFilterChange('job_title', e.target.value)}
            >
              <option value="All Jobs">All Jobs</option>
              {availableJobs.map(job => (
                <option key={job.job_id} value={job.job_title}>
                  {job.job_title}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="per-page">Candidates per page:</label>
            <select 
              id="per-page"
              value={filters.per_page}
              onChange={(e) => handleFilterChange('per_page', parseInt(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="comp-jobs-list-container" style={{ maxHeight: `${maxHeight - 150}px` }}>
        <div className="comp-jobs-list">
          {candidates.length === 0 ? (
            <div className="no-candidates">No candidates found</div>
          ) : (
            candidates.map((candidate, index) => (
              <CandidateCard 
                key={`${candidate.email}-${candidate.job_id}`} 
                candidate={candidate} 
                onReject={handleReject} 
              />
            ))
          )}
        </div>
      </div>
      
      {/* Pagination */}
      {totalCount > filters.per_page && (
        <div className="pagination">
          <button 
            disabled={filters.page === 1}
            onClick={() => handlePageChange(filters.page - 1)}
          >
            Previous
          </button>
          
          <span className="page-info">
            Page {filters.page} of {Math.ceil(totalCount / filters.per_page)}
          </span>
          
          <button 
            disabled={filters.page >= Math.ceil(totalCount / filters.per_page)}
            onClick={() => handlePageChange(filters.page + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default CandidateFollowup;